
import Vue from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { ChatMessage } from "../models/ChatMessage";

// Open every link in a bot message in a new tab so navigating away
// (e.g. to a company/model page) never clears the current chat session.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

export default Vue.extend({
  name: "ChatMessageBubble",
  props: {
    message: {
      type: Object as () => ChatMessage,
      required: true,
    },
  },
  data() {
    return {
      copied: false,
      copyTimer: undefined as number | undefined,
    };
  },
  computed: {
    renderedContent(): string {
      if (this.message.role !== "bot") return "";
      const raw = marked.parse(this.message.content);
      return DOMPurify.sanitize(String(raw));
    },
    formattedTime(): string {
      return new Date(this.message.timestamp).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  beforeDestroy() {
    window.clearTimeout(this.copyTimer);
  },
  methods: {
    async copyContent() {
      const text = this.message.content;
      try {
        // navigator.clipboard is undefined outside a secure context (HTTPS / localhost).
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          this.fallbackCopy(text);
        }
      } catch {
        this.fallbackCopy(text);
      }
      this.copied = true;
      window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(() => {
        this.copied = false;
      }, 1500);
    },
    fallbackCopy(text: string) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    },
  },
});
